import React, { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import ContentHeader from '@Components/ContentHeader'
import TabsWrapperComponent from '@Components/TabsWrapperComponent'
import { StoresContext } from '@App/contexts'
import {
    getSongwriterSubmissionSuggestions,
    updateSongwriterSubmissionSuggestionStatus,
} from '@App/api/routes/rightsholder/change-requests'
import { PaginationBody } from '@Entities/pagination'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import {
    type SongwriterSubmissionSuggestion,
    SongwriterSubmissionSuggestionStatus,
} from '@Entities/rightsholder/songwriter-submission-suggestion'
import './index.scss'
import { type EnrichedSuggestion } from '@Scenes/RightsholderScene/ChangeRequests/build-table-rows'
import ChangeRequestTable from '@Scenes/RightsholderScene/ChangeRequests/ChangeRequestTable/index'
import type { ResultPage } from '@Entities/result-page'
import { getWorkById } from '@App/api/routes/work'

const defaultPagination: PaginationBody = { page: 1, limit: 10 }

interface HistoryItemsState {
    page: ResultPage<SongwriterSubmissionSuggestion>
    items: EnrichedSuggestion[]
}

const loadSuggestionsWithWorks = async (
    rightsholderId: number,
    statuses: SongwriterSubmissionSuggestionStatus[],
    pagination: PaginationBody,
    onRequestItemsChange: (historyItems: HistoryItemsState | undefined) => void,
    onRequestPaginationChange: (pagination: PaginationBody) => void,
    onError: (error: unknown) => void
) => {
    try {
        const response = await getSongwriterSubmissionSuggestions(rightsholderId, statuses, pagination)
        const items = await Promise.all(
            response.data.content.map(async (suggestion) => {
                const workResponse = await getWorkById(suggestion.submissionId, FetchType.MLC, rightsholderId)
                return { ...suggestion, work: workResponse?.data }
            })
        )

        onRequestItemsChange({ page: response.data, items })
        onRequestPaginationChange(pagination)
    } catch (error) {
        onError(error)
    }
}

const ChangeRequests = () => {
    const { rightsholderStore, popupStore } = useContext(StoresContext)
    const [reviewItems, setReviewItems] = useState<HistoryItemsState>()
    const [historyItems, setHistoryItems] = useState<HistoryItemsState>()
    const [reviewPagination, setReviewPagination] = useState<PaginationBody>(defaultPagination)
    const [historyPagination, setHistoryPagination] = useState<PaginationBody>(defaultPagination)
    const [loading, setLoading] = useState(false)

    const rightsholderId = rightsholderStore.currentRightsholderId

    const loadReview = async (pagination: PaginationBody = reviewPagination) => {
        await loadSuggestionsWithWorks(
            rightsholderId,
            ['SUBMITTED'],
            pagination,
            setReviewItems,
            setReviewPagination,
            popupStore.setAlertFromError
        )
    }

    const loadHistory = async (pagination: PaginationBody = historyPagination) => {
        await loadSuggestionsWithWorks(
            rightsholderId,
            ['APPROVED', 'DISMISSED'],
            pagination,
            setHistoryItems,
            setHistoryPagination,
            popupStore.setAlertFromError
        )
    }

    const runWithLoading = async (method: () => Promise<void>) => {
        setLoading(true)
        try {
            await method()
        } finally {
            setLoading(false)
        }
    }

    const loadAll = async (
        nextReviewPagination: PaginationBody = reviewPagination,
        nextHistoryPagination: PaginationBody = historyPagination
    ) => {
        await Promise.all([loadReview(nextReviewPagination), loadHistory(nextHistoryPagination)])
    }

    const dismissSuggestion = async (suggestionId: number) => {
        await runWithLoading(async () => {
            try {
                await updateSongwriterSubmissionSuggestionStatus(rightsholderId, suggestionId, 'DISMISSED')
                await loadAll()
            } catch (e) {
                popupStore.setAlertFromError(e)
            }
        })
    }

    useEffect(() => {
        void runWithLoading(() => loadAll(defaultPagination, defaultPagination))
    }, [rightsholderId])

    const tabs = useMemo(
        () => [
            {
                tabName: 'For My Review',
                tabDisplayName: 'For My Review',
                tabComponent: (
                    <ChangeRequestTable
                        items={reviewItems?.items ?? []}
                        totalResults={reviewItems?.page.totalElements ?? 0}
                        currentPage={reviewPagination.page ?? 1}
                        limit={reviewPagination.limit ?? 10}
                        onPageChange={(pagination) => loadReview(pagination)}
                        onDismiss={dismissSuggestion}
                    />
                ),
            },
            {
                tabName: 'History',
                tabDisplayName: 'History',
                tabComponent: (
                    <ChangeRequestTable
                        items={historyItems?.items ?? []}
                        totalResults={historyItems?.page.totalElements ?? 0}
                        currentPage={historyPagination.page ?? 1}
                        limit={historyPagination.limit ?? 10}
                        onPageChange={(pagination) => loadHistory(pagination)}
                    />
                ),
            },
        ],
        [reviewItems, reviewPagination, historyItems, loading, historyPagination]
    )

    return (
        <div className="change-requests-scene">
            <ContentHeader
                title="Change Requests"
                descriptionElement="This is your notification dashboard. Here, you'll see requests from songwriters you work with. After reviewing the request type, you'll need to take action in other parts of the portal. Once completed, you can return here to mark the task as done, or dismiss it if you don't want to take action."
            />
            <TabsWrapperComponent tabs={tabs} defaultTab={tabs[0]} />
        </div>
    )
}

export default observer(ChangeRequests)
